import type { FC, ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import type { FormikHelpers } from 'formik';
import { FieldArray, useFormikContext } from 'formik';
import type {
  BatchReceiptDetails,
  LineItem,
  ReceiptBatchFormValues,
  TaxItem,
} from './ServiceProviderReceiptBatchForm';
import { Box, Button, Card, Divider, Stack, Typography } from '@mui/material';
import { Iconify } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import type { Gender } from '@pflegenavi/shared/api';
import { useTaxItemsAndDefault } from '../useDefaultTaxItem';

export interface ServiceProviderReceiptBatchRenderRowProps {
  receipt: BatchReceiptDetails;
  index: number;
  removeRow: (index: number) => void;
  setFieldValue: FormikHelpers<ReceiptBatchFormValues>['setFieldValue'];
  setShownImageId: (imgId: string | undefined) => void;
  setPreviewImageIds: (imgIds: string[]) => void;
  lastInputRef?: React.RefObject<HTMLInputElement | null>;
  archivedResidents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
}

interface ReceiptBatchReceiptsProps {
  renderRow: (props: ServiceProviderReceiptBatchRenderRowProps) => ReactNode;
  setShownImageId: (imgId: string | undefined) => void;
  setPreviewImageIds: (imgIds: string[]) => void;
  ReceiptBatchQuickResidentSelection?: React.ReactNode;
  archivedResidents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
}

interface CreateNewLineItemProps {
  taxes?: TaxItem[];
}

export const createNewLineItem = (opts?: CreateNewLineItemProps): LineItem => ({
  id: undefined,
  _internalId: `new-${uuid()}`,
  amount: undefined,
  count: 1,
  name: undefined,
  taxes: opts?.taxes,
});

interface CreateNewReceiptProps {
  lineItemOpts?: CreateNewLineItemProps;
}

export const createNewReceipt = (
  opts?: CreateNewReceiptProps
): BatchReceiptDetails => ({
  id: `new-${uuid()}`,
  residentId: undefined,
  receiptImageIds: [],
  residentActive: true,
  individualDate: undefined,
  lineItems: [createNewLineItem(opts?.lineItemOpts)],
});

export const ServiceProviderReceiptBatchReceiptsView: FC<
  ReceiptBatchReceiptsProps
> = ({
  renderRow,
  setShownImageId,
  setPreviewImageIds,
  ReceiptBatchQuickResidentSelection,
  archivedResidents,
}) => {
  const { t } = useTranslation();
  const {
    values: { receipts, nursingHomeId },
    setFieldValue,
  } = useFormikContext<ReceiptBatchFormValues>();

  const lastInputRef = useRef(null);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (
        event.key === 'Tab' &&
        !event.shiftKey &&
        event.target === lastInputRef.current
      ) {
        event.preventDefault();

        setFieldValue('receipts', [...receipts, createNewReceipt()]);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [lastInputRef, receipts, setFieldValue]);

  const { defaultTaxCode } = useTaxItemsAndDefault();

  if (!nursingHomeId) {
    return null;
  }

  return (
    <Card sx={{ p: 2 }}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
          }}
        >
          {t('receipts.batch.form.details')}
        </Typography>
        {ReceiptBatchQuickResidentSelection}
      </Stack>
      <Divider
        sx={{
          width: '100%',
          mb: 4,
        }}
      />
      <FieldArray name="receipts">
        {({ remove, push }) => (
          <Box sx={{ overflowX: 'auto' }}>
            {receipts.map((receipt, index) => {
              return renderRow({
                index,
                receipt,
                removeRow: remove,
                setFieldValue,
                setShownImageId,
                setPreviewImageIds,
                archivedResidents,
                lastInputRef:
                  index === receipts.length - 1 ? lastInputRef : undefined,
              });
            })}
            <Button
              variant="text"
              size="small"
              onClick={() =>
                push(
                  createNewReceipt({
                    lineItemOpts: {
                      taxes: defaultTaxCode ? [defaultTaxCode] : undefined,
                    },
                  })
                )
              }
              startIcon={<Iconify icon={'ic:baseline-plus'} />}
            >
              {t('receipts.batch.form.add-resident')}
            </Button>
          </Box>
        )}
      </FieldArray>
    </Card>
  );
};
